import { removeLeadingSlash } from '@desygner/ui-common-svg-utils';
import { ProjectPayload } from '@desygner/ui-data-access-projects';

/**
 * Extracts the project ID from a URL pathname based on the URL pattern.
 * Supports three URL patterns:
 * - Design URLs: /:project_id (e.g., /hOfRd9FBTWB)
 * - Template URLs: /preview/template/:project_id (e.g., /preview/template/hOfRd9FBTWB)
 * - Preview Design URLs: /preview/design/:project_id (e.g., /preview/design/hOfRd9FBTWB)
 *
 * @param url - The URL object to extract the ID from
 * @returns The project ID string, or empty string if no valid pattern is found
 */
export function extractIdFromUrl(url: URL): string {
  const pathParts = url.pathname.split('/').filter(Boolean);

  // Check for design URL pattern: /:project_id
  const isDesignURLPattern = pathParts.length === 1 && !!pathParts[0];

  // Check for template URL pattern: /preview/template/:project_id
  const isTemplateURLPattern =
    pathParts.length === 3 &&
    pathParts[0] === 'preview' &&
    pathParts[1] === 'template' &&
    !!pathParts[2];

  // Check for preview design URL pattern: /preview/design/:project_id
  const isPreviewDesignURLPattern =
    pathParts.length === 3 &&
    pathParts[0] === 'preview' &&
    pathParts[1] === 'design' &&
    !!pathParts[2];

  if (isDesignURLPattern) {
    return pathParts[0];
  }

  if (isTemplateURLPattern) {
    return pathParts[2];
  }

  if (isPreviewDesignURLPattern) {
    return pathParts[2];
  }

  return '';
}

/**
 * Determines the project type based on the URL pattern.
 * Template URLs start with '/preview/template/', preview design URLs start with '/preview/design/',
 * all others are considered designs.
 *
 * @param url - The URL object to determine the project type from
 * @returns 'template' for template URLs, 'design' for all others including preview design URLs
 */
export function getProjectTypeFromUrl(url: URL): ProjectPayload['type'] {
  if (url.pathname.startsWith('/preview/template/')) {
    return 'template';
  }

  // Both /preview/design/:project and /:project are treated as 'design' type
  return 'design';
}

export function getExitDomainUrl(baseUrl: string, workspace?: string, params?: string): string {
  const cleanedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

  if (!workspace) return cleanedBaseUrl;

  const [protocol, domain] = cleanedBaseUrl.split('//');

  // remove the last / in the domain if exists
  const domainParts = domain.split('.').filter((item, index) => index !== 0 || item !== 'editor');

  return `${protocol}//${workspace}.${domainParts.join('.')}${params ?? ''}`;
}

export function isAbsoluteUrl(url: string): boolean {
  try {
    return Boolean(new URL(url).protocol);
  } catch {
    return false;
  }
}

export function normalizePath(path: string): string {
  if (!path || path === '/') {
    return '';
  }

  return `/${removeLeadingSlash(path)}`;
}

export function buildBrandUrl(baseDomainUrl: string, path?: string): string {
  if (!path) {
    return baseDomainUrl;
  }

  if (isAbsoluteUrl(path)) {
    return path;
  }

  return `${baseDomainUrl}${normalizePath(path)}`;
}
